import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { LazyImage } from '@/components/ui/lazy-image';
import { Database } from '@/integrations/supabase/types';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { ReadingTime } from '@/components/blog/ReadingTime';
import { calculateReadingTime } from '@/lib/blog-utils';
import { Luggage, MapPin } from 'lucide-react';


// Using a more flexible type definition
type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string; category?: string }[];
};

interface BlogPostCardProps {
  post: BlogPost;
  priority?: boolean;
}

export function BlogPostCard({ post, priority = false }: BlogPostCardProps) {
  const hasPackingList = Boolean((post as any).hasPackingList);
  const hasItineraries = Boolean((post as any).hasItineraries);
  
  const formattedDate = post.created_at 
    ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) 
    : '';
    
  const authorName = post.author 
    ? `${post.author.first_name || ''} ${post.author.last_name || ''}`.trim() 
    : 'Trotting the World';

  // Calculate reading time
  const readingTimeMinutes = post.content 
    ? calculateReadingTime(post.content) 
    : 0;

  // Group tags by category
  const groupTagsByCategory = () => {
    const grouped: Record<string, typeof post.tags> = {};
    
    post.tags?.forEach((tag) => {
      const category = tag.category || 'uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category]?.push(tag);
    });
    
    return grouped;
  };
  
  const tagsByCategory = groupTagsByCategory();

  return (
    <Card className="overflow-hidden group h-full flex flex-col">
      <Link to={`/blog/${post.slug}`} className="flex-1 flex flex-col h-full">
        <div className="aspect-video relative overflow-hidden">
          {post.featured_image ? (
            <LazyImage 
              src={post.featured_image} 
              alt={post.title || 'Blog post image'} 
              className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              priority={priority}
              blurUpPreview={true}
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <span className="text-muted-foreground">No image</span>
            </div>  
          )}
          
          {/* Feature Indicators */}
          <div className="absolute top-2 right-2 z-10 flex flex-col gap-2">
            {hasPackingList && (
              <Badge className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600">
                <Luggage className="h-3.5 w-3.5" />
                <span>Packing Guide</span>
              </Badge>
            )}
            
            {hasItineraries && (
              <Badge className="flex items-center gap-1 bg-primary hover:bg-primary/80">
                <MapPin className="h-3.5 w-3.5" />
                <span>With Itinerary</span>
              </Badge>
            )}
          </div>
        </div>
        <CardContent className="p-4 flex-1">
          <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {post.title}
          </h2>
          {post.excerpt && (
            <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
              {post.excerpt}
            </p>
          )}
          
          {/* Tags - grouped by category */}
          {post.tags && post.tags.length > 0 && (
            <div className="space-y-1.5 mt-2 mb-1">
              {Object.entries(tagsByCategory).map(([category, categoryTags], index) => (
                <div key={category} className={`flex flex-wrap gap-1 ${index > 0 ? 'mt-1' : ''}`}>
                  {category !== 'uncategorized' && (
                    <Badge variant="outline" className="text-xs bg-muted/50">
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </Badge>
                  )}
                  {categoryTags?.slice(0, 2).map(tag => (
                    <Badge 
                      key={tag.id} 
                      variant="secondary" 
                      className="text-xs"
                    >
                      {tag.name}
                    </Badge>
                  ))}
                  {categoryTags && categoryTags.length > 2 && (
                    <span className="text-xs text-muted-foreground">+{categoryTags.length - 2} more</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="px-4 pb-4 pt-0 flex items-center text-xs text-muted-foreground justify-between">
          <div className="flex items-center gap-2">
            <span>{authorName}</span>
            {readingTimeMinutes > 0 && (
              <ReadingTime minutes={readingTimeMinutes} />
            )}
          </div>
          <time dateTime={post.created_at || ''}>{formattedDate}</time>
        </CardFooter>
      </Link>
    </Card>
  );
}
